'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Image } from '@/component/Image';
import { Link } from '@/i18n/routing';
import { HeaderContent } from '@/components/base/Layout/Header/HeaderContent';
import { skipToken, useSuspenseQuery } from '@apollo/client';
import { MenuItemsQuery } from '@/query';

function Error({ statusCode, withHeader = true }) {
  const navigation = [
    { name: 'menu.first', href: '/#search-box' },
    { name: 'menu.second', href: '/onboarding' },
    { name: 'about.hero.title', href: '/about' },
    {
      name: 'main.sitemap.title',
      href: '/sitemap',
      class: 'mt-8 !text-slate-500 !text-base !font-normal',
    },
  ];

  const t = useTranslations();
  const locale = useLocale();

  const { data } = useSuspenseQuery(
    MenuItemsQuery,
    withHeader
      ? {
          variables: { locale },
          errorPolicy: 'ignore',
        }
      : skipToken
  );

  return (
    <>
      {withHeader && <HeaderContent menuItems={data?.menuItems} />}
      <section className="container flex-1 max-sm:py-6 sm:max-lg:py-8 lg:py-[56px] border-t border-slate-200 !bg-slate-50 3xl:rounded-10 3xl:border-none 3xl:max-h-[750px]">
        <div className="max-sm:py-6 sm:py-8 sm:max-lg:px-16 lg:px-20">
          <div className="flex max-lg:flex-col max-sm:gap-4 sm:gap-[56px] min-h-[540px]">
            <div className="lg:w-1/2">
              <h1 className="text-teal-800 max-sm:text-[64px] sm:text-8xl font-bold mb-[18px] leading-[normal]">
                Oops!
              </h1>
              <h2 className="text-slate-600 text-2xl font-semibold leading-[30px] tracking-tight max-sm:mb-[22px] sm:max-lg:mb-8 lg:mb-[46px]">
                {t(
                  `${
                    statusCode !== 404
                      ? 'error.server.title'
                      : 'error.client.title'
                  }`
                )}
              </h2>
              <p className="text-slate-600 text-lg font-normal leading-7 mb-2.5">
                {t(
                  `${
                    statusCode !== 404
                      ? 'error.server.description'
                      : 'error.client.description'
                  }`
                )}
              </p>
              <ul role="list" className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      target={item.target}
                      className={`text-teal-700 text-lg font-semibold underline inline-flex ${item.class}`}
                    >
                      {t(item.name)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/2 relative sm:min-w-[340px] max-sm:min-h-[350px] sm:min-h-[540px] inline-flex items-center justify-center">
              <Image
                className="max-sm:min-w-[350px] max-sm:w-[350px] sm:min-w-[540px] sm:w-[540px] max-sm:h-[350px] sm:h-[540px] max-sm:absolute object-cover lg:absolute top-0"
                src={`https://guestit-web-assets.s3-accelerate.amazonaws.com/images/icons/${
                  statusCode !== 404
                    ? 'guestit-error-mascot'
                    : 'guestit-404-mascot'
                }.svg`}
                alt={t('error.client.title')}
                title={t('error.client.title')}
                width={540}
                height={540}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Error;
